import { z } from '@hono/zod-openapi';

import { RequestIdSchema } from './common';
import { CONTRACT_VERSION } from './versions';

export const API_ERROR_CODES = [
  'INVALID_REQUEST',
  'INVALID_CREDENTIAL',
  'FEATURE_DISABLED',
  'ENTITLEMENT_REQUIRED',
  'PAYLOAD_TOO_LARGE',
  'RATE_LIMITED',
  'REVISION_CONFLICT',
  'IDEMPOTENCY_CONFLICT',
  'PRESET_STALE',
  'BACKUP_TAMPERED',
  'ROOM_FULL',
  'ROOM_BANNED',
  'ROOM_EXPIRED',
  'MEDIA_UNAVAILABLE',
  'EXTRACT_FAILED',
  'DEPENDENCY_UNAVAILABLE',
  'NOT_FOUND',
  'INTERNAL',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const ERROR_POLICIES = {
  INVALID_REQUEST: { status: 400, retryable: false },
  INVALID_CREDENTIAL: { status: 401, retryable: false },
  FEATURE_DISABLED: { status: 403, retryable: false },
  ENTITLEMENT_REQUIRED: { status: 403, retryable: false },
  PAYLOAD_TOO_LARGE: { status: 413, retryable: false },
  RATE_LIMITED: { status: 429, retryable: true },
  REVISION_CONFLICT: { status: 409, retryable: false },
  IDEMPOTENCY_CONFLICT: { status: 409, retryable: false },
  PRESET_STALE: { status: 409, retryable: true },
  BACKUP_TAMPERED: { status: 422, retryable: false },
  ROOM_FULL: { status: 409, retryable: true },
  ROOM_BANNED: { status: 403, retryable: false },
  ROOM_EXPIRED: { status: 410, retryable: false },
  MEDIA_UNAVAILABLE: { status: 503, retryable: true },
  EXTRACT_FAILED: { status: 502, retryable: true },
  DEPENDENCY_UNAVAILABLE: { status: 503, retryable: true },
  NOT_FOUND: { status: 404, retryable: false },
  INTERNAL: { status: 500, retryable: true },
} as const satisfies Record<ApiErrorCode, { status: number; retryable: boolean }>;

export const ApiErrorSchema = z
  .object({
    error: z
      .object({
        code: z.enum(API_ERROR_CODES),
        message: z.string().min(1).max(300),
        requestId: RequestIdSchema,
        retryable: z.boolean(),
        details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.error.retryable !== ERROR_POLICIES[value.error.code].retryable) {
      context.addIssue({
        code: 'custom',
        path: ['error', 'retryable'],
        message: 'retryable must match the central error policy',
      });
    }
  })
  .openapi('ApiError');

export type ApiErrorEnvelope = z.infer<typeof ApiErrorSchema>;

export function createApiError(
  code: ApiErrorCode,
  message: string,
  requestId: string,
  details?: Readonly<Record<string, string | number | boolean>>,
): ApiErrorEnvelope {
  return ApiErrorSchema.parse({
    error: {
      code,
      message,
      requestId,
      retryable: ERROR_POLICIES[code].retryable,
      ...(details === undefined ? {} : { details }),
    },
  });
}

export const HealthSchema = z
  .object({
    status: z.literal('ok'),
    service: z.literal('hoedokloop-api'),
    revision: z.string().nullable().optional(),
    contractVersion: z.literal(CONTRACT_VERSION),
    requestId: RequestIdSchema,
  })
  .strict()
  .openapi('Health');

export const ReadySchema = z
  .object({
    status: z.enum(['ready', 'not_ready']),
    checked: z.array(z.enum(['DB', 'PRESETS', 'BACKUPS', 'PRODUCT_ANALYTICS', 'ROOMS'])),
    missing: z.array(z.string()),
    requestId: RequestIdSchema,
  })
  .strict()
  .openapi('Readiness');

export const API_ROUTES = [
  ['POST', '/v1/devices'],
  ['POST', '/v1/devices/rotate'],
  ['DELETE', '/v1/devices/current'],
  ['GET', '/v1/presets/exams'],
  ['POST', '/v1/events'],
  ['GET', '/healthz'],
  ['GET', '/readyz'],
  ['POST', '/v1/backup-accounts'],
  ['POST', '/v1/backup-accounts/link'],
  ['POST', '/v1/backup-accounts/rotate'],
  ['POST', '/v1/backups'],
  ['GET', '/v1/backups'],
  ['GET', '/v1/backups/:id'],
  ['DELETE', '/v1/backups/:id'],
  ['DELETE', '/v1/backup-accounts/current'],
  ['PUT', '/v1/notifications/subscription'],
  ['DELETE', '/v1/notifications/subscription'],
  ['PUT', '/v1/notifications/rules'],
  ['POST', '/v1/extract'],
  ['GET', '/v1/entitlements/me'],
  ['POST', '/v1/purchases/verify'],
  ['POST', '/v1/purchases/restore'],
  ['GET', '/v1/rooms'],
  ['POST', '/v1/rooms'],
  ['GET', '/v1/rooms/:id'],
  ['POST', '/v1/rooms/:id/join'],
  ['POST', '/v1/rooms/:id/presence'],
  ['POST', '/v1/rooms/:id/leave'],
  ['POST', '/v1/rooms/:id/reports'],
  ['POST', '/v1/rooms/:id/members/:memberId/kick'],
  ['POST', '/v1/rooms/:id/reactions'],
] as const;
